// Encapsulates library-side flow: pick a game, create a room, navigate to /room/:code

import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useAuth } from './useAuth';
import { useMedia } from './useMedia';
import { analyticsService } from '../services/analyticsService';
import { createRoom } from '../transport/api/protectedApi';
import {
  HostStyleConfig,
  RoomUser,
  type GameLibraryConfig,
} from '@airhead-games/shared/types';
import { createLogger } from '@airhead-games/shared/utilities';

const logger = createLogger('useRoomGameSelect');

interface UseRoomGameSelectResult {
  selectGame: (game: GameLibraryConfig) => Promise<void>;
}

export const useRoomGameSelect = (): UseRoomGameSelectResult => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { setIsMuted, isMuted, resumeAudioContext } = useMedia();

  const selectGame = useCallback(
    async (game: GameLibraryConfig) => {
      try {
        // Resume synchronously within the gesture so audio starts on room mount. Without this, the await on createRoom
        // can outlast the gesture's user-activation window, leaving the context suspended until click in room.
        void resumeAudioContext();

        if (!user) {
          analyticsService.trackButtonClick('Select Game', {
            game_id: game.gameCore.gameId,
            game_name: game.gameCore.gameName,
            link_location: 'library_select_game',
            auth_required: true,
          });
          const nextParams = new URLSearchParams(searchParams);
          nextParams.set('dialog', 'login');
          nextParams.delete('step');
          setSearchParams(nextParams, { replace: true });
          return;
        }

        const loonCost = game.gameCore.loonCost ?? 0;
        if (loonCost > 0 && (user.loonBalance ?? 0) < loonCost) {
          analyticsService.trackButtonClick('Select Game', {
            game_id: game.gameCore.gameId,
            game_name: game.gameCore.gameName,
            link_location: 'library_select_game',
            insufficient_loons: true,
          });
          const nextParams = new URLSearchParams(searchParams);
          nextParams.set('dialog', 'buy-loons');
          setSearchParams(nextParams, { replace: true });
          return;
        }

        const roomUser: RoomUser = {
          userId: user._id,
          userPreferences: {
            language: user.userPreferences.language,
            accent: user.userPreferences.accent,
            hostStyle: {
              personality: user.userPreferences.hostStyle
                .personality as HostStyleConfig['personality'],
              gender: user.userPreferences.hostStyle.gender,
              voiceActor: user.userPreferences.hostStyle
                .voiceActor as HostStyleConfig['voiceActor'],
            },
            contentStyle: user.userPreferences.contentStyle,
          },
          modSettings: {
            censorCommon: user.modSettings.censorCommon,
            censorCustom: user.modSettings.censorCustom,
            censorRigid: user.modSettings.censorRigid,
            censorCustomWords: user.modSettings.censorCustomWords,
            twitchTermsBlocked: user.modSettings.twitchTermsBlocked ?? false,
            censorTwitchWords: user.modSettings.censorTwitchWords ?? [],
            twitchPlayersOnly: user.modSettings.twitchPlayersOnly,
            disallowBlockedAndBanned:
              user.modSettings.disallowBlockedAndBanned ?? false,
            blockedTwitchUserIds: (
              user.modSettings.blockedTwitchUsers ?? []
            ).map((blockedUser) => blockedUser.twitchUserId),
          },
        };

        logger.info('Requesting room creation...');
        const res = await createRoom(roomUser, game.gameCore.gameId);
        if (!res.success) {
          throw new Error('Failed to create room');
        }

        const { room } = res.data;
        const roomCode = room.roomCode;

        analyticsService.trackButtonClick('Select Game', {
          game_id: game.gameCore.gameId,
          game_name: game.gameCore.gameName,
        });

        analyticsService.trackRoomCreated({
          game_name: game.gameCore.gameName,
          room_code: roomCode,
        });

        logger.info('Navigating to room', roomCode);
        navigate(`/room/${roomCode}`);

        if (isMuted) {
          setIsMuted(false);
        }
      } catch (error) {
        logger.error('Error creating room or starting game:', error);
      }
    },
    [
      isMuted,
      navigate,
      resumeAudioContext,
      searchParams,
      setIsMuted,
      setSearchParams,
      user,
    ]
  );

  return { selectGame };
};
